import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import BlockConfirmationModal from "../../../components/template/Modal/BlockConfirmationModal";
import ASSET_PATHS from "../../../assets/config/assetPaths";
import { getImage } from "../../../db";
import Layout from "../../../components/template/Layout";

interface QueueEntry {
  workOrderId?: string;
  assetId?: string;
  pointId?: string;
  label?: string;
  type?: string;
  fileName?: string;
  image?: string; //base 64
  description?: string;
  module?: string;
}

const OfflineSyncQueue: React.FC = () => {
  const [groupedQueue, setGroupedQueue] = useState<
    Record<string, QueueEntry[]>
  >({});
  const [loading, setLoading] = useState<boolean>(true);
  const [deleteItem, setDeleteItem] = useState<QueueEntry | null>(null);
  const [showClearAllModal, setShowClearAllModal] = useState<boolean>(false);
  const [resolvedImages, setResolvedImages] = useState<Record<string, string>>(
    {}
  );

  const navigate = useNavigate();
  const normalizeType = (type: string): string => {
    const typeMapping: Record<string, string> = {
      imageUpload: "Image Upload",
      updateInspectionPoint: "Update Inspection Point",
      updateAssetStatus: "Update Asset Status",
      createAsset: "Create Asset",
      updateInspectionToProgress: "Update Inspection To Progress",
    };
    return typeMapping[type] || "type not defined";
  };
  const handleCloseDeleteModal = () => {
    setDeleteItem(null);
  };
  const handleDeleteItem = async () => {
    if (!deleteItem) return;

    try {
      setLoading(true);
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "ITEM_ACTION",
          payload: {
            workOrderId: deleteItem.workOrderId,
            assetId: deleteItem.assetId,
            pointId: deleteItem.pointId,
            fileName: deleteItem.fileName,
            actionType: "delete",
          },
        });
      }
    } catch (error) {
      console.error("Error deleting item:", error);
    } finally {
      setLoading(false);
      handleCloseDeleteModal();
    }
  };

  const handleClearAll = async () => {
    try {
      setLoading(true);
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "CLEAR_ALL_PENDING_REQUESTS",
        });
      }
    } catch (error) {
      console.error("Error clearing all items:", error);
    } finally {
      setLoading(false);
      setShowClearAllModal(false);
    }
  };

  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      console.log(
        "[OFFLINE_SYNC_QUEUE]: Received message from service worker:",
        event?.data
      );
      if (event?.data?.type === "FAILED_REQUEST") {
        const grouped = event.data.payload?.items.reduce(
          (acc: Record<string, QueueEntry[]>, item: QueueEntry) => {
            const type = item.type || "type not defined";
            if (!acc[type]) acc[type] = [];
            acc[type].push(item);
            return acc;
          },
          {}
        );
        setGroupedQueue(grouped);
        setLoading(false);
        console.log("failed requests data", event.data.payload);
      }
    };
    navigator.serviceWorker.ready
      .then(() => {
        navigator.serviceWorker.addEventListener(
          "message",
          handleServiceWorkerMessage
        );
        navigator.serviceWorker.controller?.postMessage({
          type: "SYNC_STATUS",
        });
        navigator.serviceWorker.controller?.postMessage({
          type: "FAILED_REQUEST",
        });
      })
      .catch((error) => {
        console.error("Service worker not ready:", error);
        setLoading(false);
      });

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage
      );
    };
  }, []);

  useEffect(() => {
    const loadImageURLs = async () => {
      const urlMap: Record<string, string> = {};
      const seen = new Set<string>();

      Object.values(groupedQueue)
        .flat()
        .forEach((entry) => {
          if (entry.image && !seen.has(entry.image)) {
            seen.add(entry.image);
          }
        });

      await Promise.all(
        Array.from(seen).map(async (uuid) => {
          try {
            const result = await getImage(uuid);
            if (result?.blob) {
              urlMap[uuid] = URL.createObjectURL(result.blob);
            }
          } catch (e) {
            console.warn("Failed to load image for UUID:", uuid);
          }
        })
      );

      setResolvedImages((prev) => ({ ...prev, ...urlMap }));
    };

    loadImageURLs();
  }, [groupedQueue]);

  const handleItemClick = (
    workOrderId?: string,
    assetId?: string,
    pointId?: string,
    fileName?: string,
    actionType?: string,
    type?: string
  ) => {
    if (!navigator.serviceWorker.controller) {
      console.error("service Worker is not active.");
      return;
    }
    if (
      (workOrderId && assetId && actionType) ||
      type == "Update Inspection To Progress" ||
      actionType == "retry"
    ) {
      if (actionType == "edit") {
        if (workOrderId && assetId) {
          navigate(`/work-orders/${workOrderId}/inspection/${assetId}`);
        }
      } else {
        if (actionType == "delete") {
          setDeleteItem({
            workOrderId,
            assetId,
            pointId,
            fileName,
            type: actionType,
          });
        } else {
          setLoading(true);
          navigator.serviceWorker.controller.postMessage({
            type: "ITEM_ACTION",
            payload: {
              workOrderId,
              assetId,
              pointId,
              fileName,
              actionType,
            },
          });
          console.log(
            `sent action '${actionType}' for WorkOrder ${workOrderId} Asset ${assetId} Point ${pointId} to Service Worker.`
          );
          setTimeout(() => {
            setLoading(false);
          }, 3000);
        }
      }
    } else {
      console.error(
        "missing data: WorkOrderId, AssetId, PointId, or ActionType."
      );
    }
  };

  const handleDownload = async (uuid: string) => {
    try {
      const result = await getImage(uuid);
      if (!result?.blob) throw new Error("Blob not found");

      const url = URL.createObjectURL(result.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${uuid}.jpg`; // or .png, or use `entry.fileName` if available
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download image:", err);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(resolvedImages).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <BlockConfirmationModal
        isOpen={!!deleteItem}
        onClose={handleCloseDeleteModal}
        onConfirm={handleDeleteItem}
        isSkipMode={false}
        message={`Are you sure you want to delete this request?`}
      />
      {/* Clear All Confirmation Modal */}
      <BlockConfirmationModal
        isOpen={showClearAllModal}
        onClose={() => setShowClearAllModal(false)}
        onConfirm={handleClearAll}
        isSkipMode={false}
        message={`Are you sure you want to delete all pending requests?`}
      />
      <Layout title="Pending Requests"  showBackIcon>
        {Object.keys(groupedQueue).length > 0 && (
          <div className="flex justify-end p-2">
            <button
              onClick={() => setShowClearAllModal(true)}
              className="bg-red-500 text-white px-3 mx-3 py-1 text-sm rounded-md hover:bg-red-600"
            >
              Clear All
            </button>
          </div>
        )}
        <div className="w-full p-4">
          <div className="bg-white rounded-lg w-full shadow p-4">
            {loading ? (
              <div className="flex justify-center items-center">
                <div className="w-8 h-8 border-4 border-t-transparent border-primary rounded-full animate-spin"></div>
              </div>
            ) : Object.keys(groupedQueue).length > 0 ? (
              Object.entries(groupedQueue)
                .flatMap(([type, entries]) =>
                  entries.map((entry) => ({
                    ...entry,
                    type: normalizeType(type),
                  }))
                )
                .map((entry, index) => (
                  <div
                    key={index}
                    className="border border-gray-300 rounded-lg p-4 bg-gray-100 shadow-sm hover:shadow-md mb-4"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        {/* <p className="text-sm text-gray-600">
                        <strong>Type:</strong> {entry.type || "Unknown Type"}
                      </p> */}
                        <p className="text-sm text-gray-600">
                          <strong>Work Order:</strong>{" "}
                          {entry.workOrderId || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Item:</strong> {entry.assetId || "N/A"}
                        </p>
                        {entry.module && (
                          <p className="text-sm text-gray-600">
                            <strong>Module :</strong> {entry.module || "N/A"}
                          </p>
                        )}
                        <p className="text-sm text-gray-600">
                          <strong>Point:</strong> {entry.label || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          <strong>Description:</strong>{" "}
                          {entry.description || "N/A"}
                        </p>
                      </div>
                    </div>
                    {entry.image && resolvedImages[entry.image] && (
                      <div className="mt-4">
                        <img
                          src={resolvedImages[entry.image]}
                          alt="Asset Preview"
                          className="w-48 h-48 object-cover"
                        />
                      </div>
                    )}
                    <div className="flex justify-center items-center space-x-4 mt-4">
                      <button
                        onClick={() =>
                          handleItemClick(
                            entry.workOrderId,
                            entry.assetId,
                            entry.pointId,
                            entry.fileName,
                            "retry"
                          )
                        }
                        className="p-2"
                      >
                        <img
                          src={ASSET_PATHS.refresh}
                          alt="Refresh"
                          className="w-6 h-6"
                        />
                      </button>
                      {entry.type != "Update Inspection To Progress" && (
                        <button
                          onClick={() =>
                            handleItemClick(
                              entry.workOrderId,
                              entry.assetId,
                              entry.pointId,
                              entry.fileName,
                              "edit"
                            )
                          }
                          className="p-2"
                        >
                          <img
                            src={ASSET_PATHS.editBlue}
                            alt="Edit"
                            className="w-6 h-6"
                          />
                        </button>
                      )}
                      <button
                        onClick={() =>
                          handleItemClick(
                            entry.workOrderId,
                            entry.assetId,
                            entry.pointId,
                            entry.fileName,
                            "delete",
                            entry.type
                          )
                        }
                        className="p-2"
                      >
                        <img
                          src={ASSET_PATHS.trash}
                          alt="Delete"
                          className="w-6 h-6"
                        />
                      </button>
                      {entry?.image && (
                        <button
                          onClick={() =>
                            handleDownload(entry?.image ? entry.image : "")
                          }
                          className="p-2"
                        >
                          <img
                            src={ASSET_PATHS.download}
                            alt="Download"
                            className="w-6 h-6"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                ))
            ) : (
              <p className="text-center text-gray-600">No pending requests.</p>
            )}
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default OfflineSyncQueue;
